import { useMemo } from 'react';

/**
 * useAccountManagerMetrics
 * 
 * Calculates all metrics for Account Manager dashboard.
 * Separate from recruiter metrics due to different business logic:
 * - Uses assigned_clients directly (no team lead inheritance)
 * - Tracks jobs where AM is credited (account_manager field)
 * - Different pipeline filtering logic
 * 
 * @param {Object} params
 * @returns {Object} - All calculated Account Manager metrics
 */
export const useAccountManagerMetrics = ({
  jobs,
  submissions,
  selectedUser,
  selectedClients,
  selectedTimeframe
}) => {
  
  const ACTION_REQUIRED_JOB_STATUSES = ['accepting_candidates', 'interviewing'];
  const ACTIVE_JOB_STATUSES = ['accepting_candidates', 'covered', 'interviewing', 'offer_out'];

  // ============================================================================
  // VISIBILITY: Jobs for assigned clients
  // ============================================================================
  const accountManagerJobs = useMemo(() => {
    const hasAssignedClients = selectedUser.assigned_clients && selectedUser.assigned_clients.length > 0;
    
    if (!hasAssignedClients) {
      return jobs.filter(job => {
        const jobAM = job.account_manager || '';
        return jobAM === selectedUser.full_name || jobAM === selectedUser.name;
      });
    }
    
    const filteredJobs = jobs.filter(job => {
      const isAssignedClient = job.client_company && 
                               selectedUser.assigned_clients.includes(job.client_company);
      
      if (selectedClients.size > 0) {
        return isAssignedClient && selectedClients.has(job.client_company);
      }
      
      return isAssignedClient;
    });
    
    return filteredJobs;
  }, [jobs, selectedUser, selectedClients]);

  // Get unique clients for filter dropdown
  const accountManagerClients = useMemo(() => {
    const clients = new Set();
    accountManagerJobs.forEach(job => {
      if (job.client_company) clients.add(job.client_company);
    });
    return Array.from(clients).sort();
  }, [accountManagerJobs]);

  // ============================================================================
  // PERFORMANCE METRICS: Jobs where AM is credited
  // ============================================================================
  const accountManagerOwnedJobs = useMemo(() => {
    return jobs.filter(job => {
      const jobAM = job.account_manager || '';
      return jobAM === selectedUser.full_name || jobAM === selectedUser.name;
    });
  }, [jobs, selectedUser]);

  // ============================================================================
  // CURRENT OPEN JOBS
  // ============================================================================
  const amCurrentOpenJobs = useMemo(() => {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    
    return accountManagerJobs.filter(job => {
      const status = (job.status || '').toLowerCase();
      if (!ACTION_REQUIRED_JOB_STATUSES.includes(status)) return false;
      
      // Use date_added or fall back to created_at
      const dateAdded = job.date_added || job.created_at;
      if (!dateAdded) return true; // Include jobs without dates (don't filter out)
      
      try {
        const jobDate = new Date(dateAdded);
        // Check if date is valid
        if (isNaN(jobDate.getTime())) return true; // Include invalid dates
        if (jobDate < sixMonthsAgo) return false;
      } catch (error) {
        // If date parsing fails, include the job anyway
        return true;
      }
      
      return true;
    });
  }, [accountManagerJobs, ACTION_REQUIRED_JOB_STATUSES]);

  // ============================================================================
  // CURRENT PIPELINE
  // ============================================================================
  const amActiveSubs = useMemo(() => {
    const activeJobIds = amCurrentOpenJobs.map(j => j.id);
    return submissions.filter(sub => activeJobIds.includes(sub.job_id));
  }, [amCurrentOpenJobs, submissions]);

  // ============================================================================
  // TIME-FILTERED ACTIVITY METRICS
  // ============================================================================
  const getDateRange = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    switch (selectedTimeframe) {
      case 'ytd':
        return { start: new Date(currentYear, 0, 1), end: now };
      case 'this_month':
        return { start: new Date(currentYear, now.getMonth(), 1), end: now };
      case 'last_month':
        const lastMonth = new Date(currentYear, now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(currentYear, now.getMonth(), 0);
        return { start: lastMonth, end: lastMonthEnd };
      case 'last_6_months':
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        return { start: sixMonthsAgo, end: now };
      case 'last_12_months':
        const twelveMonthsAgo = new Date(now);
        twelveMonthsAgo.setMonth(now.getMonth() - 12);
        return { start: twelveMonthsAgo, end: now };
      default:
        return { start: new Date(currentYear, 0, 1), end: now };
    }
  };

  const amTimeFilteredJobs = useMemo(() => {
    const { start, end } = getDateRange();
    return accountManagerOwnedJobs.filter(job => {
      const jobDate = job.date_added ? new Date(job.date_added) : null;
      return jobDate && jobDate >= start && jobDate <= end;
    });
  }, [accountManagerOwnedJobs, selectedTimeframe]);

  const amFilledJobs = useMemo(() => {
    return amTimeFilteredJobs.filter(job => job.status === 'filled');
  }, [amTimeFilteredJobs]);

  return {
    accountManagerJobs,
    accountManagerClients,
    amCurrentOpenJobs,
    amActiveSubs,
    amTimeFilteredJobs,
    amFilledJobs
  };
};

export default useAccountManagerMetrics;
